import { Icon } from '@natera/material/lib/icon'
import { useEobOnboardingSelectors } from '~/features/eob-onboarding'
import { apiClient } from '~/shared/api/data-ingestion-api'
import { cn } from '~/shared/lib/cn'
import { useIsScrolled } from '~/shared/lib/use-is-scrolled'
import { IconButton, Popover, PopoverAnchor, PopoverContent, Text } from '~/shared/ui'
import { PatientButton } from './patient-button'

interface ClaimsSidebarProps {
  claims: apiClient.PatientClaimResponse[]
  selectedClaimKey: string | null
  onSelectClaim: (key: string) => void
  onAddClaim: () => void
  getDisplayName: (claim: apiClient.PatientClaimResponse) => string | null
  isReviewed: (claim: apiClient.PatientClaimResponse) => boolean
  getFlagSeverity?: (claim: apiClient.PatientClaimResponse) => 'high' | 'low' | null
}

export function ClaimsSidebar({
  claims,
  selectedClaimKey,
  onSelectClaim,
  onAddClaim,
  getDisplayName,
  isReviewed,
  getFlagSeverity,
}: ClaimsSidebarProps) {
  const { isScrolled: isScrolledTop, sentinelElement: sentinelTop } = useIsScrolled()
  const { isScrolled: isScrolledBottom, sentinelElement: sentinelBottom } = useIsScrolled()
  const {
    isPatientClaimOnboardingOpen,
    setIsPatientClaimOnboardingOpen,
    markPatientClaimOnboardingSeen,
  } = useEobOnboardingSelectors()
  const handleDismissOnboarding = () => {
    setIsPatientClaimOnboardingOpen(false)
    markPatientClaimOnboardingSeen()
  }

  return (
    <div className="border-divider relative flex w-16 shrink-0 flex-col border-r">
      <div className="flex h-[60px] w-16 shrink-0 items-center justify-center">
        <Popover
          open={isPatientClaimOnboardingOpen}
          onOpenChange={(open) => {
            if (!open) handleDismissOnboarding()
          }}
        >
          <PopoverAnchor asChild>
            <button
              onClick={onAddClaim}
              className="bg-primary-main text-content-highest-emphasis-inverse size-10 cursor-pointer rounded-full border-none p-0 shadow-[0_0.5px_1.75px_0_rgba(0,0,0,0.04),0_1.85px_6.25px_0_rgba(0,0,0,0.19)]"
            >
              <Icon children="add" />
            </button>
          </PopoverAnchor>
          <PopoverContent
            side="right"
            align="start"
            sideOffset={18}
            alignOffset={-4}
            onOpenAutoFocus={(e) => e.preventDefault()}
            className="flex w-[280px] items-start gap-4 px-4 py-3"
          >
            <div className="flex min-w-0 flex-1 flex-col gap-2">
              <Text appearance="SUBTITLE2">Keep Rolling</Text>
              <Text
                className="text-text-secondary"
                appearance="CAPTION"
              >
                Great start! Ready for more? Hit the “+” button to add the next patient.
              </Text>
            </div>
            <IconButton
              size="small"
              container="small"
              materialIcon="close"
              onClick={handleDismissOnboarding}
              className="absolute top-2 right-2"
            />
          </PopoverContent>
        </Popover>
      </div>
      <div className="relative min-h-0 flex-1">
        <div
          className={cn(
            'from-surface-lowest pointer-events-none absolute inset-x-0 top-0 z-10 h-4 bg-linear-to-b to-transparent transition-opacity',
            isScrolledTop ? 'opacity-100' : 'opacity-0',
          )}
        />
        <div
          className={cn(
            'from-surface-lowest pointer-events-none absolute inset-x-0 bottom-0 z-10 h-4 bg-linear-to-t to-transparent transition-opacity',
            isScrolledBottom ? 'opacity-100' : 'opacity-0',
          )}
        />
        <div className="flex h-full flex-col items-center overflow-y-auto pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {sentinelTop}
          {claims.map((claim) => (
            <PatientButton
              key={claim.key}
              name={getDisplayName(claim)}
              selected={selectedClaimKey === claim.key}
              reviewed={isReviewed(claim)}
              flagSeverity={getFlagSeverity?.(claim) ?? null}
              onClick={() => onSelectClaim(claim.key)}
            />
          ))}
          {sentinelBottom}
        </div>
      </div>
    </div>
  )
}
